import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Table, Button, Space, Typography, InputNumber, Modal, message,
} from 'antd';
import {
  ArrowLeftOutlined, ShoppingCartOutlined, DeleteOutlined, HistoryOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { oilOrdersApi, OilSupplier, OilProduct, OilOrderResponse } from '../api/oilOrders';
import type { CartChangeItem } from '../api/oilOrders';
import useOilCartStore from '../store/oilCartStore';
import useTaskStore from '../store/taskStore';
import CartValidationModal from '../components/CartValidationModal';

const { Title, Text } = Typography;

const PALETTE = [
  { bg: '#f0f7ff', border: '#ddeaf6' },
  { bg: '#f2f9f2', border: '#d4ead2' },
  { bg: '#f5f0fb', border: '#e0d8f0' },
  { bg: '#fdf5f0', border: '#ede0d8' },
];

interface SummaryItem {
  key: string;
  num: number;
  name: string;
  sizeId: number;
  supplierId: number;
  origin: string | null;
  qty: number;
  price: number;
  sum: number;
}

export default function OilOrdersCartPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<OilSupplier[]>([]);
  const [products, setProducts] = useState<OilProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const { items: cartItems, loadCart, setQty, clearCart } = useOilCartStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const tasks = useTaskStore(s => s.tasks);

  // Модал валидации корзины
  const [cartValidationOpen, setCartValidationOpen] = useState(false);
  const [cartChanges, setCartChanges] = useState<CartChangeItem[]>([]);
  const [cartValidationTaskId, setCartValidationTaskId] = useState<number | null>(null);

  const cartPriceTaskId = useMemo(() => {
    const t = tasks.find(t => t.type === 'cart_price_changes' && t.status === 'done');
    return t ? t.id : null;
  }, [tasks]);

  const openCartValidation = useCallback(() => {
    const t = tasks.find(t => t.type === 'cart_price_changes' && t.status === 'done');
    if (!t) return;
    setCartChanges(t.result_json?.changes as CartChangeItem[] ?? []);
    setCartValidationTaskId(t.id);
    setCartValidationOpen(true);
  }, [tasks]);

  // query param cart_validation=1 (из TaskBell)
  useEffect(() => {
    if (searchParams.get('cart_validation') === '1' && cartPriceTaskId) {
      openCartValidation();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, cartPriceTaskId]);

  // Автооткрытие при появлении новой задачи
  const prevCartTaskRef = React.useRef<number | null>(null);
  useEffect(() => {
    if (cartPriceTaskId && cartPriceTaskId !== prevCartTaskRef.current) {
      prevCartTaskRef.current = cartPriceTaskId;
      openCartValidation();
    }
    if (!cartPriceTaskId) {
      prevCartTaskRef.current = null;
    }
  }, [cartPriceTaskId, openCartValidation]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sups, prods] = await Promise.all([
        oilOrdersApi.getSuppliers(),
        oilOrdersApi.getProducts(),
      ]);
      setSuppliers(sups);
      setProducts(prods);
      await loadCart();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Итоги ───
  const fmtMoney = (v: number) => v.toLocaleString('ru', { minimumFractionDigits: 2 }) + ' ₽';

  let totalQty = 0, totalSum = 0;
  for (const ci of cartItems) {
    if (ci.qty > 0) totalQty += ci.qty;
    for (const prod of products) {
      const sz = prod.sizes.find(s => s.id === ci.size_id);
      if (sz) {
        const pr = sz.prices.find(p => p.supplier_id === ci.supplier_id && p.origin === ci.origin);
        if (pr && ci.qty > 0) totalSum += ci.qty * pr.price;
        break;
      }
    }
  }

  // ─── Группировка по поставщикам ───
  const summaries: { id: number; name: string; items: SummaryItem[]; totalQty: number; totalSum: number }[] = [];
  let gQty = 0, gSum = 0;

  for (const sup of suppliers) {
    const supItems = cartItems.filter(ci => ci.supplier_id === sup.id);
    if (!supItems.length) continue;

    const items: SummaryItem[] = supItems.map((ci, idx) => {
      let itemName = '', unitPrice = 0;
      for (const prod of products) {
        const sz = prod.sizes.find(s => s.id === ci.size_id);
        if (sz) {
          itemName = [prod.brand, prod.product_types?.[0], prod.model_name, ci.origin, sz.container_size].filter(Boolean).join(' ');
          const pr = sz.prices.find(p => p.supplier_id === ci.supplier_id && p.origin === ci.origin);
          if (pr) unitPrice = pr.price;
          break;
        }
      }
      return {
        key: `${sup.id}-${ci.size_id}-${ci.origin ?? ''}`,
        num: idx + 1,
        name: itemName,
        sizeId: ci.size_id,
        supplierId: ci.supplier_id,
        origin: ci.origin,
        qty: ci.qty,
        price: unitPrice,
        sum: ci.qty * unitPrice,
      };
    });

    const tQty = items.reduce((s, i) => s + (i.qty > 0 ? i.qty : 0), 0);
    const tSum = items.reduce((s, i) => s + (i.qty > 0 ? i.sum : 0), 0);
    summaries.push({ id: sup.id, name: sup.name, items, totalQty: tQty, totalSum: tSum });
    gQty += tQty;
    gSum += tSum;
  }

  // ─── Оформление заказа ───
  const handlePlaceOrder = () => {
    Modal.confirm({
      title: 'Разместить заказ?',
      content: `Итого: ${totalQty} шт на сумму ${totalSum.toLocaleString('ru', { minimumFractionDigits: 2 })} ₽`,
      okText: 'Разместить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const res: OilOrderResponse = await oilOrdersApi.placeOrder();
          message.success(`Заказ #${res.id} оформлен: ${res.total_qty} шт на ${res.total_sum.toLocaleString('ru', { minimumFractionDigits: 2 })} ₽`);
          await clearCart();
          await loadCart();
          navigate('/oil-orders/history', { state: { nevaOrderId: res.id, nevaItems: res.items } });
        } catch {
          message.error('Ошибка при размещении заказа');
        }
      },
    });
  };

  // ─── Колонки таблицы ───
  const columns = [
    { title: '№', dataIndex: 'num', width: 50, align: 'center' as const },
    { title: 'Наименование', dataIndex: 'name' },
    {
      title: 'Кол-во', dataIndex: 'qty', width: 100, align: 'right' as const,
      render: (_: number, row: SummaryItem) => (
        <InputNumber
          size="small"
          min={1}
          value={row.qty}
          style={{ width: 70 }}
          onChange={(v) => { if (v != null && v >= 1) setQty(row.sizeId, row.supplierId, row.origin, v); }}
        />
      ),
    },
    {
      title: 'Цена', dataIndex: 'price', width: 110, align: 'right' as const,
      render: (v: number) => v ? fmtMoney(v) : '',
    },
    {
      title: 'Сумма', dataIndex: 'sum', width: 130, align: 'right' as const,
      render: (_: number, row: SummaryItem) => {
        const s = row.qty * row.price;
        return s > 0 ? fmtMoney(s) : '';
      },
    },
    {
      title: '', dataIndex: 'action', width: 40, align: 'center' as const,
      render: (_: any, row: SummaryItem) => (
        <Button
          type="text"
          size="small"
          danger
          icon={<CloseOutlined />}
          onClick={() => setQty(row.sizeId, row.supplierId, row.origin, 0)}
          style={{ opacity: row.qty === 0 ? 0.3 : 1 }}
          disabled={row.qty === 0}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Шапка */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 24,
      }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/oil-orders')}>Каталог</Button>
          <Title level={3} style={{ margin: 0 }}>
            <ShoppingCartOutlined style={{ marginRight: 8 }} />
            Корзина
          </Title>
        </Space>
        <Space>
          <Text strong style={{ fontSize: 16 }}>
            {totalQty} шт · {totalSum.toLocaleString('ru', { minimumFractionDigits: 2 })} ₽
          </Text>
          <Button icon={<HistoryOutlined />} onClick={() => navigate('/oil-orders/history')}>
            История
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => {
            Modal.confirm({
              title: 'Очистить корзину?',
              okText: 'Очистить', cancelText: 'Отмена', okType: 'danger',
              onOk: async () => { await clearCart(); },
            });
          }}>Очистить</Button>
          <Button type="primary" onClick={handlePlaceOrder} disabled={totalQty === 0}>
            Разместить заказ
          </Button>
        </Space>
      </div>

      {/* Сводные таблицы по поставщикам */}
      {loading ? null : summaries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Text type="secondary" style={{ fontSize: 16 }}>Корзина пуста</Text>
        </div>
      ) : (
        <div style={{ maxWidth: 800 }}>
          {summaries.map(ss => {
            const sIdx = suppliers.findIndex(x => x.id === ss.id);
            const color = PALETTE[(sIdx >= 0 ? sIdx : 0) % PALETTE.length];
            return (
              <div key={ss.id} style={{
                marginBottom: 20,
                // background: color.bg, border: `1px solid ${color.border}`,
                borderRadius: 8, padding: '12px 16px',
              }}>
                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 16 }}>{ss.name}</Text>
                <Table
                  className="oil-cart-summary"
                  columns={columns}
                  dataSource={ss.items}
                  size="small"
                  pagination={false}
                  bordered
                  rowClassName={(row: SummaryItem) => row.qty === 0 ? 'oil-cart-zero' : ''}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2} align="right"><Text strong>Итого</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right"><Text strong>{ss.totalQty} шт</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={3} />
                      <Table.Summary.Cell index={4} align="right"><Text strong>{fmtMoney(ss.totalSum)}</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={5} />
                    </Table.Summary.Row>
                  )}
                />
              </div>
            );
          })}
          {summaries.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, padding: '8px 0' }}>
              <Text strong>ОБЩИЙ ИТОГ</Text>
              <Text strong>{gQty} шт</Text>
              <Text strong>{fmtMoney(gSum)}</Text>
            </div>
          )}
        </div>
      )}

      <style>{`
        tr.oil-cart-zero > td { color: #bbb !important; }
        tr.oil-cart-zero .ant-input-number { opacity: 0.5; }
        .oil-cart-summary .ant-table-thead > tr > th { font-weight: 400 !important; color: #888 !important; }
      `}</style>

      <CartValidationModal
        open={cartValidationOpen}
        changes={cartChanges}
        taskId={cartValidationTaskId}
        onClose={() => setCartValidationOpen(false)}
        onResolved={() => {
          setCartValidationOpen(false);
          setCartChanges([]);
          setCartValidationTaskId(null);
          loadData();
        }}
      />
    </div>
  );
}
